import React from "react";
import { fmtTime, locale, WEEKDAYS, weekdayShort } from "./tariffs";

const svg = (children) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

export const Icons = {
  hour: svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  day: svg(
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  night: svg(<path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />),
  nightsub: svg(
    <>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
      <path d="M16 3l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" />
    </>
  ),
  workday: svg(
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </>
  ),
  full: svg(<path d="M12 2l3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z" />),
  pin: svg(
    <>
      <path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </>
  ),
  car: svg(
    <>
      <path d="M5 17h14v-4l-2-6H7l-2 6z" />
      <path d="M5 13h14M6 17v2M18 17v2" />
    </>
  ),
  mail: svg(
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </>
  ),
  plate: svg(
    <>
      <rect x="3" y="8" width="18" height="8" rx="2" />
      <path d="M7 12h2M11 12h6" />
    </>
  ),
  card: svg(
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </>
  ),
  gate: svg(
    <>
      <path d="M4 4h16v16H4z" />
      <path d="M9 9h6v6H9z" />
    </>
  ),
  code: svg(
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M10 16V8h3a2.5 2.5 0 0 1 0 5h-3" />
    </>
  ),
  back: svg(<path d="M15 6l-6 6 6 6" />),
  check: svg(<path d="M5 12.5l4.5 4.5L19 7.5" />),
  lock: svg(
    <>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </>
  ),
  info: svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16h.01" />
    </>
  ),
  locate: svg(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
      <circle cx="12" cy="12" r="7" />
    </>
  ),
};

export const QrGraphic = () => (
  <svg viewBox="0 0 29 29" aria-hidden="true">
    <path
      fill="#001e5e"
      d="M0 0h7v7H0zM2 2v3h3V2zM22 0h7v7h-7zm2 2v3h3V2zM0 22h7v7H0zm2 2v3h3v-3zM9 0h2v2H9zm4 0h4v2h-2v2h-2zm-4 4h2v3H9zm6 0h2v2h-2zm2 2h2v2h-2zm-8 3h4v2H9zm6 0h2v4h-2zm4 0h3v2h-3zm5 0h5v2h-5zM0 9h5v2H0zm7 2h2v2H7zm-7 2h3v2H0zm11 0h2v2h-2zm4 2h4v2h-4zm6-2h2v4h-2zm4 0h4v2h-4zM9 15h2v4H9zm4 2h2v2h-2zm12 0h4v2h-4zM9 21h4v2H9zm6-2h2v4h-2zm4 2h4v2h-4zm6 0h2v2h-2zM9 25h2v4H9zm4 0h4v2h-2v2h-2zm6 0h2v4h-2zm4 2h2v2h-2zm4-2h2v4h-2z"
    />
  </svg>
);

export const tariffName = (tariff, t) => tariff.name || t(`rxT_${tariff.type}`);

const hhmm = (s, lang) => {
  if (!s) return "";
  if (s === "24:00") return "24:00";
  const [h, m] = s.split(":").map(Number);
  const d = new Date(2024, 0, 1, h, m || 0);
  return fmtTime(d, lang);
};

export const weekdayRange = (days, lang) => {
  if (!days || days.length === 7) return null;
  const sorted = [...days].sort();
  const contiguous = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
  if (contiguous && sorted.length > 2) return `${weekdayShort(sorted[0], lang)} – ${weekdayShort(sorted[sorted.length - 1], lang)}`;
  return sorted.map((d) => weekdayShort(d, lang)).join(", ");
};

// Kurzbeschreibung, wann ein Tarif gilt
export const tariffWhen = (tariff, t, lang) => {
  const range = `${hhmm(tariff.start, lang)} – ${hhmm(tariff.end, lang)}`;
  switch (tariff.type) {
    case "hour":
      return tariff.maxPerDay ? t("rxW_hourMax", { max: tariff.maxPerDay.toLocaleString(locale(lang), { style: "currency", currency: "EUR" }) }) : t("rxW_hour");
    case "day":
      return t("rxW_day");
    case "night":
      return t("rxW_night", { range });
    case "nightsub":
      return t("rxW_nightsub", { range });
    case "workday":
      return t("rxW_days", { days: weekdayRange(tariff.weekdays, lang) || t("rxDaily"), range });
    case "full":
      return tariff.weekdays && tariff.weekdays.length < 7 ? t("rxW_days", { days: weekdayRange(tariff.weekdays, lang), range: t("rxAllDay") }) : t("rxW_full");
    default:
      return "";
  }
};

export const unitLabel = (tariff, t) => t(`rxU_${tariff.type}`);

export const WeekdayPicker = ({ value, allowed, onChange, lang }) => (
  <div className="rx-days">
    {WEEKDAYS.map((d) => {
      const enabled = !allowed || allowed.includes(d);
      return (
        <label key={d} className={enabled ? "" : "is-off"}>
          <input
            type="checkbox"
            disabled={!enabled}
            checked={enabled && value.includes(d)}
            onChange={(e) => onChange(e.target.checked ? [...value, d].sort() : value.filter((x) => x !== d))}
          />
          <span>{weekdayShort(d, lang)}</span>
        </label>
      );
    })}
  </div>
);
